/**
 * ErrorBoundary — stops a single component error from white-screening the app.
 *
 * Without this, any render-time throw in a page (a bad selector, a null deref,
 * an adapter edge case) takes the whole app down to a blank screen. With it,
 * the rest of the shell survives and the user sees a recoverable error card.
 */

import React from 'react';

interface Props { children: React.ReactNode }
interface State { error: Error | null }

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface to the console for debugging; in production this would go to Sentry/etc.
    console.error('[DataFlow] view crashed:', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div style={{
          maxWidth: 560, margin: '48px auto', padding: 24,
          background: 'var(--bg-2)', border: '1px solid var(--red-border)',
          borderRadius: 12, color: 'var(--text)', boxShadow: 'var(--shadow)',
        }}>
          <h2 style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--red)' }}>
            Something went wrong on this view
          </h2>
          <p style={{ fontSize: 13.5, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
            The rest of the app is fine. You can retry this view or switch to another tab above.
          </p>
          <pre style={{
            fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--text-3)',
            background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
            padding: '8px 10px', overflowX: 'auto', margin: '0 0 14px',
          }}>{this.state.error.message}</pre>
          <button
            onClick={this.reset}
            style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
